import { useState, useMemo, useCallback, useRef } from 'react';
import { Printer, RotateCcw, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import MJLogo from '../MJLogo';
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ComposedChart, Line
} from 'recharts';

/* ──────────────── constants ──────────────── */
const SIEVE_ROWS = [
  { slNo: 1, micron: '1700', mesh: '10',  multiplier: 5 },
  { slNo: 2, micron: '850',  mesh: '20',  multiplier: 10 },
  { slNo: 3, micron: '600',  mesh: '30',  multiplier: 20 },
  { slNo: 4, micron: '425',  mesh: '40',  multiplier: 30 },
  { slNo: 5, micron: '300',  mesh: '50',  multiplier: 40 },
  { slNo: 6, micron: '212',  mesh: '70',  multiplier: 50 },
  { slNo: 7, micron: '150',  mesh: '100', multiplier: 70 },
  { slNo: 8, micron: '106',  mesh: '140', multiplier: 100 },
  { slNo: 9, micron: '75',   mesh: '200', multiplier: 140 },
  { slNo: 10, micron: '53',  mesh: '270', multiplier: 200 },
  { slNo: 11, micron: 'PAN', mesh: 'PAN', multiplier: 270 },
];

const CONSIGNEE_OPTIONS = [
  'SRI BALAJI CASTINGS PVT LTD',
  'SVE CASTINGS PVT LTD',
];

const COMPANY = {
  name: 'M&J ENTERPRISES',
  address: '1-B, Rajupalem, Ozili Mandalam, Tirupathi DIST. AP-524402',
};

const Y_TICKS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20];

const INITIAL_RETAINED = () => SIEVE_ROWS.map(() => '');

/* ──────────────── component ──────────────── */
export default function LabReport() {
  const [consignee, setConsignee] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [vehicleNo, setVehicleNo] = useState('');
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [afsSpec, setAfsSpec] = useState('35-40');
  const [note, setNote] = useState(
    'SiO2 content testing report should be provided once in a month, if testing facility not available should be tested through third party NABL certified Lab'
  );
  const [retained, setRetained] = useState(INITIAL_RETAINED);
  const retainedRefs = useRef([]);

  /* ── derived calculations ── */
  const calculations = useMemo(() => {
    const rows = SIEVE_ROWS.map((s, i) => {
      const ret = parseFloat(retained[i]) || 0;
      const product = +(ret * s.multiplier).toFixed(2);
      return { ...s, retained: ret, product };
    });
    const totalRetained = +rows.reduce((a, r) => a + r.retained, 0).toFixed(2);
    const totalProduct = +rows.reduce((a, r) => a + r.product, 0).toFixed(2);
    const afs = totalRetained > 0 ? +(totalProduct / totalRetained).toFixed(2) : 0;
    return { rows, totalRetained, totalProduct, afs };
  }, [retained]);

  const chartData = useMemo(() => {
    return calculations.rows.map((r) => ({
      name: r.mesh === 'PAN' ? 'PAN' : `${r.mesh}`,
      retained: r.retained,
    }));
  }, [calculations.rows]);

  const handleRetainedChange = useCallback((index, value) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setRetained((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleRetainedKeyDown = useCallback((index, e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      e.preventDefault();
      const next = index + 1;
      if (next < SIEVE_ROWS.length) {
        retainedRefs.current[next]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = index - 1;
      if (prev >= 0) {
        retainedRefs.current[prev]?.focus();
      }
    }
  }, []);

  const handleReset = () => {
    setRetained(INITIAL_RETAINED());
    setConsignee('');
    setVehicleNo('');
    setReportDate(new Date().toISOString().slice(0, 10));
    setAfsSpec('35-40');
  };

  const displayDate = reportDate ? reportDate.split('-').reverse().join('-') : '';

  return (
    <div data-testid="lab-report">
      {/* Action Bar — screen only */}
      <div className="flex items-center justify-between mb-4 no-print">
        <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Silica Sand Test Report
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} data-testid="lab-report-reset" className="gap-1.5 text-slate-600 border-slate-300">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Button>
          <Button size="sm" onClick={() => window.print()} data-testid="lab-report-print" className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-white border-0">
            <Printer className="w-3.5 h-3.5" /> Print
          </Button>
        </div>
      </div>

      {/* ═══════ Printable Report ═══════ */}
      <div id="lab-report-printable" className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm lab-report-sheet">

        {/* ── 1. Company Header ── */}
        <div className="lab-header-bar" style={{ background: 'linear-gradient(to right, #f59e0b, #d97706)', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
          <div className="flex items-center gap-3 px-5 py-3">
            <div style={{ background: '#fff', borderRadius: '50%', padding: '4px', boxShadow: '0 2px 6px rgba(0,0,0,.15)' }}>
              <MJLogo size={40} />
            </div>
            <div>
              <h1 style={{ color: '#1e293b', fontSize: '18px', fontWeight: 800, fontFamily: "'Manrope', sans-serif", letterSpacing: '0.5px' }}>
                {COMPANY.name}
              </h1>
              <p style={{ color: '#78350f', fontSize: '11px', marginTop: '1px' }}>{COMPANY.address}</p>
            </div>
          </div>
        </div>

        {/* Title Bar */}
        <div className="lab-title-bar" style={{ background: '#1e293b', textAlign: 'center', padding: '6px 0', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
          <h2 style={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', margin: 0 }}>
            Silica Sand Test Report
          </h2>
        </div>

        <div className="px-5 py-3 lab-body" style={{ fontSize: '12px' }}>

          {/* ── 2. Header Fields ── */}
          {/* Screen: stacked on mobile, row on desktop */}
          <div className="lab-header-fields mb-3" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
            <div className="flex items-center gap-2 relative lab-field-row">
              <span className="lab-field-label">Consignee Name :</span>
              <div className="relative flex-1 no-print">
                <div className="flex gap-1">
                  <Input
                    data-testid="lab-consignee"
                    value={consignee}
                    onChange={(e) => setConsignee(e.target.value)}
                    placeholder="Enter or select client"
                    className="lab-field-input h-7 text-sm flex-1"
                    style={{ fontSize: '12px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="h-7 w-7 flex items-center justify-center border border-slate-300 rounded-md hover:bg-slate-50 flex-shrink-0"
                  >
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>
                {showDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-50">
                    {CONSIGNEE_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        data-testid={`consignee-option-${opt.split(' ')[0].toLowerCase()}`}
                        onClick={() => { setConsignee(opt); setShowDropdown(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
                        style={{ fontSize: '12px' }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 lab-field-row">
              <span className="lab-field-label">Vehicle No :</span>
              <Input data-testid="lab-vehicle" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="AP39VB7619" className="lab-field-input h-7 text-sm flex-1" />
            </div>
            <div className="flex items-center gap-2 lab-field-row">
              <span className="lab-field-label">Date :</span>
              <Input data-testid="lab-date" type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="lab-field-input h-7 text-sm flex-1" />
            </div>
          </div>
          {/* Print-only: single row always */}
          <div className="lab-print-header-row">
            <span className="lab-field-label">Consignee Name :</span>
            <span className="lab-ph-val">{consignee || '\u00A0'}</span>
            <span className="lab-field-label" style={{ marginLeft: '24px' }}>Vehicle No :</span>
            <span className="lab-ph-val">{vehicleNo || '\u00A0'}</span>
            <span className="lab-field-label" style={{ marginLeft: '24px' }}>Date :</span>
            <span className="lab-ph-val">{displayDate || '\u00A0'}</span>
          </div>

          {/* ── 3. Sieve Analysis Table ── */}
          <div className="mb-2 lab-table-scroll">
            <table className="w-full lab-table" data-testid="sieve-table" style={{ borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: '520px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(to right, #f59e0b, #d97706)', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                  <th className="lab-th" style={{ width: '16.66%', textAlign: 'center' }}>SL No</th>
                  <th className="lab-th" style={{ width: '16.66%', textAlign: 'center' }}>Mesh Micron NS</th>
                  <th className="lab-th" style={{ width: '16.66%', textAlign: 'center' }}>Mesh</th>
                  <th className="lab-th lab-th-retained" style={{ width: '16.66%', textAlign: 'center' }}>Retained</th>
                  <th className="lab-th" style={{ width: '16.66%', textAlign: 'center' }}>Multiples</th>
                  <th className="lab-th" style={{ width: '16.66%', textAlign: 'center' }}>Product</th>
                </tr>
              </thead>
              <tbody>
                {SIEVE_ROWS.map((row, idx) => {
                  const product = calculations.rows[idx]?.product || 0;
                  return (
                    <tr key={row.slNo} className={idx % 2 === 0 ? 'lab-row-even' : 'lab-row-odd'}>
                      <td className="lab-td" style={{ textAlign: 'center', color: '#94a3b8' }}>{row.slNo}</td>
                      <td className="lab-td" style={{ textAlign: 'center' }}>{row.micron}</td>
                      <td className="lab-td" style={{ textAlign: 'center', fontWeight: 600 }}>{row.mesh}</td>
                      <td className="lab-td lab-td-retained">
                        <input
                          ref={(el) => { retainedRefs.current[idx] = el; }}
                          data-testid={`retained-${idx}`}
                          value={retained[idx]}
                          onChange={(e) => handleRetainedChange(idx, e.target.value)}
                          onKeyDown={(e) => handleRetainedKeyDown(idx, e)}
                          placeholder="0"
                          className="lab-retained-input"
                        />
                        <span className="lab-retained-value">{retained[idx] || '0'}</span>
                      </td>
                      <td className="lab-td" style={{ textAlign: 'center', color: '#64748b' }}>{row.multiplier}</td>
                      <td className="lab-td" style={{ textAlign: 'center', fontWeight: 600, color: '#1e293b' }}>
                        {product.toFixed(1)}
                      </td>
                    </tr>
                  );
                })}
                {/* Total */}
                <tr style={{ background: '#1e293b', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
                  <td colSpan={3} style={{ padding: '6px 8px', textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Total</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', color: '#fff', fontWeight: 700 }} data-testid="total-retained">{calculations.totalRetained.toFixed(2)}</td>
                  <td style={{ padding: '6px 8px' }}></td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', color: '#fff', fontWeight: 700 }} data-testid="total-product">{calculations.totalProduct.toFixed(1)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── AFS Result ── */}
          <div className="flex items-center gap-4 mb-2" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '6px 14px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AFS Specification :</span>
              <Input data-testid="afs-spec" value={afsSpec} onChange={(e) => setAfsSpec(e.target.value)} placeholder="35-40" className="lab-field-input h-7 text-sm w-20" />
              <span className="lab-field-value" style={{ fontWeight: 700 }}>{afsSpec}</span>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'center' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase' }}>AFS Actual : </span>
              <span data-testid="afs-actual" style={{ fontSize: '22px', fontWeight: 900, color: '#1e293b', fontFamily: "'Manrope', sans-serif" }}>
                {calculations.afs > 0 ? calculations.afs : '—'}
              </span>
            </div>
          </div>

          {/* ── Note ── */}
          <div className="mb-2" style={{ borderLeft: '3px solid #f59e0b', background: '#fffbeb', padding: '5px 10px', borderRadius: '0 6px 6px 0', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
            <span style={{ fontSize: '9px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Note : </span>
            <textarea
              data-testid="lab-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="lab-note-input"
            />
            <span className="lab-note-value" style={{ fontSize: '10px', color: '#475569' }}>{note}</span>
          </div>

          {/* ── Distribution Curve ── */}
          <div className="mb-2">
            <h3 style={{ fontSize: '10px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Distribution Curve / Graph
            </h3>
            <div className="lab-chart-wrapper" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 4px 2px 4px', background: '#fafafa', overflowX: 'auto', overflowY: 'visible' }}>
              <div style={{ minWidth: '520px' }}>
              <ComposedChart width={720} height={170} data={chartData} margin={{ top: 4, right: 10, left: 0, bottom: 14 }} className="lab-chart-svg">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" interval={0} tick={{ fontSize: 8, fill: '#334155' }} label={{ value: 'Mesh Size', position: 'insideBottom', offset: -10, fontSize: 8, fill: '#94a3b8' }} />
                <YAxis domain={[0, 20]} ticks={Y_TICKS} tick={{ fontSize: 8, fill: '#64748b' }} label={{ value: 'Retained (g)', angle: -90, position: 'insideLeft', offset: 16, fontSize: 8, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '11px', padding: '6px 10px' }} formatter={(val) => [`${val} g`, 'Retained']} labelFormatter={(l) => `Mesh ${l}`} />
                <Bar dataKey="retained" radius={[3, 3, 0, 0]} maxBarSize={28}>
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.retained > 0 ? '#f59e0b' : '#e2e8f0'} fillOpacity={entry.retained > 0 ? 0.85 : 0.3} />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="retained" stroke="#b45309" strokeWidth={1.5} dot={{ fill: '#b45309', r: 2 }} />
              </ComposedChart>
              </div>
            </div>
          </div>

          {/* ── Signature ── */}
          <div className="flex justify-end" style={{ marginTop: '140px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: '2px solid #334155', paddingTop: '2px', paddingLeft: '30px', paddingRight: '30px' }}>
                <p style={{ fontSize: '10px', fontWeight: 700, color: '#334155', margin: 0 }}>Authorized Signatory</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Print & screen styles ── */}
      <style>{`
        /* --- Screen styles for interactive inputs --- */
        .lab-field-label {
          font-size: 10px; font-weight: 700; color: #b45309;
          text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap;
        }
        .lab-field-input {
          border: 1px solid #d1d5db; border-radius: 6px; padding: 0 8px; font-size: 13px;
        }
        .lab-print-header-row { display: none; }
        .lab-ph-val { font-size: 13px; font-weight: 600; color: #1e293b; margin-left: 4px; }
        .lab-note-input {
          width: 100%; font-size: 10px; color: #475569; background: transparent;
          border: 0; padding: 0; resize: none; outline: none;
        }
        .lab-note-value { display: none; }
        .lab-retained-input {
          height: 24px; width: 70px; text-align: center; font-size: 12px;
          border: 1px solid #fde68a; border-radius: 4px; background: #fffef5;
          margin: 0 auto; display: block; outline: none;
        }
        .lab-retained-input:focus { border-color: #f59e0b; box-shadow: 0 0 0 2px rgba(245,158,11,.15); }
        .lab-retained-value { display: none; font-size: 12px; font-weight: 600; text-align: center; }

        /* Table base */
        .lab-th {
          padding: 6px 8px; text-align: left; font-size: 10px;
          font-weight: 700; text-transform: uppercase; color: #fff; letter-spacing: 0.5px;
        }
        .lab-th-retained { text-align: center; background: rgba(0,0,0,0.1); }
        .lab-td { padding: 3px 8px; font-size: 12px; color: #334155; border-bottom: 1px solid #f1f5f9; }
        .lab-td-retained { text-align: center; }
        .lab-row-even { background: #fff; }
        .lab-row-odd { background: #f8fafc; }

        /* --- MOBILE --- */
        @media (max-width: 768px) {
          .lab-header-fields {
            display: flex; flex-direction: column; gap: 8px;
          }
          .lab-field-row { width: 100%; }
          .lab-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .lab-retained-input { width: 56px; font-size: 11px; }
          .lab-th { font-size: 9px; padding: 5px 4px; }
          .lab-td { font-size: 11px; padding: 3px 4px; }
          .lab-body { padding: 10px 12px !important; }
        }
        @media (min-width: 769px) {
          .lab-header-fields {
            display: flex; flex-direction: row; gap: 24px; align-items: center;
          }
          .lab-field-row:first-child { flex: 1; }
        }

        /* --- PRINT --- */
        @media print {
          @page { size: A4 portrait; margin: 8mm 10mm; }
          html, body { margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden; }
          #lab-report-printable, #lab-report-printable * { visibility: visible; }
          #lab-report-printable {
            position: fixed; left: 0; top: 0; width: 100%;
            border: none !important; border-radius: 0 !important; box-shadow: none !important;
          }
          .no-print { display: none !important; }

          /* Force backgrounds to print */
          .lab-header-bar, .lab-title-bar, .lab-table thead tr, .lab-table tbody tr:last-child {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Swap inputs for static text in print */
          .lab-field-input, .lab-note-input, .lab-retained-input { display: none !important; }
          .lab-note-value, .lab-retained-value { display: inline !important; }

          /* Header: hide screen version, show single-row print version */
          .lab-header-fields { display: none !important; }
          .lab-print-header-row {
            display: flex !important;
            align-items: baseline;
            gap: 0;
            margin-bottom: 8px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e2e8f0;
            white-space: nowrap;
          }

          /* Compact spacing */
          .lab-body { padding: 8px 16px !important; }
          .lab-td { padding: 2px 8px !important; }
          .lab-th { padding: 4px 8px !important; }

          /* Chart print */
          .lab-chart-wrapper { overflow: visible !important; }
          .lab-chart-wrapper svg, .lab-chart-svg { width: 100% !important; height: auto !important; }
        }
      `}</style>
    </div>
  );
}
